import { stringify } from 'qs';
import React from 'react';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { NextSeo } from 'next-seo';
import { DefaultLayout } from '@layouts';
import {
  Hero,
  Highlights,
  Contacts,
  Section,
  Divider,
  Header,
  MasonryList,
  ContentBlockCard,
} from '@components';
import { Button } from '@mui/material';
import { AddRounded } from '@mui/icons-material';

const StoreDetail = ({ store }) => {
  const {
    name,
    slug,
    hero,
    contacts,
    coordinates,
    banner,
    gallery,
    highlights,
    content_block,
    seo,
  } = store;

  return (
    <>
      <NextSeo
        title={seo?.metaTitle || `Momento | ${name}`}
        description={seo?.metaDescription || hero?.description}
        additionalMetaTags={
          seo?.keywords
            ? [
                {
                  name: 'keywords',
                  content: seo?.keywords,
                },
              ]
            : []
        }
        canonical={seo?.canonicalURL}
      />

      <DefaultLayout
        ctaUrl={`/booking?store=${slug}`}
        header={
          <Hero
            isHero
            label={hero?.label}
            title={name}
            description={hero?.description}
            hasSlider={hero?.media?.data?.length > 0}
            slides={hero?.media?.data?.map(({ attributes }) => {
              return {
                img: attributes,
              };
            })}
          />
        }
      >
        <div className="pt-16 sm:pt-0"></div>
        {highlights?.length > 0 && (
          <Highlights header={null} slides={highlights} />
        )}
        <Section>
          <div className="h-4"></div>
          <div className="space-y-16">
            {content_block?.map((block, index) => {
              const { media, cta, title, description } = block;
              return (
                <ContentBlockCard
                  key={index}
                  img={
                    media?.data.length > 1
                      ? media?.data
                      : media?.data[0]?.attributes
                  }
                  index={index}
                >
                  <p className="h2 text-primary">{title}</p>

                  <p className="body max-w-[90%]">{description}</p>
                  {cta && (
                    <Button href={cta?.href ?? '/'}>
                      <AddRounded className="mr-1" />{' '}
                      <span>{cta?.label}</span>
                    </Button>
                  )}
                </ContentBlockCard>
              );
            })}
          </div>
        </Section>
        <Contacts
          contacts={contacts?.list}
          header={contacts?.header}
          coordinates={coordinates}
        />

        {banner && <Divider cta={null} {...banner} />}
        <Section>
          <Header
            title={gallery?.title}
            label={gallery?.label}
            description={gallery?.description}
          />
          <MasonryList
            gallery={gallery?.media?.data}
            showCaption={false}
          />
        </Section>
        <div className="h-20"></div>
      </DefaultLayout>
    </>
  );
};

export default StoreDetail;
export async function getServerSideProps({ params, locale }) {
  const path = process.env.NEXT_PUBLIC_STRAPI; // process.env.NEXT_PUBLIC_STRAPI;

  const qs = stringify(
    {
      populate: [
        'hero',
        'hero.media',
        'highlights.list',
        'highlights.img',
        'highlights.cta',
        'city',
        'contacts.header',
        'contacts.list',
        'content_block',
        'content_block.cta',
        'content_block.media',
        'coordinates',
        'banner',
        'banner.img',
        'gallery',
        'gallery.media',
        'seo',
        'type',
      ],
      filters: {
        slug: {
          $eq: params?.slug,
        },
      },
      locale: locale,
    },
    {
      encodeValuesOnly: true, // prettify URL
    },
  );
  const getProdData = await fetch(`${path}/stores?${qs}`);
  const product = await getProdData.json();

  if (product.data?.[0]) {
    return {
      props: {
        store: product.data?.[0]?.attributes,
        ...(await serverSideTranslations(locale, ['common'])),
        path: `${path}/stores?${qs}`,
      },
    };
  }

  return {
    redirect: {
      permanent: true,
      destination: '/',
    },
  };
}
